/*
Required Backend Endpoints

1) GET eligibility set
GET /business_waiver_builder/waiver_master/eligibility/:waiverId

2) PATCH step 5
PATCH /business_waiver_builder/waiver_master/smart_step4/:waiverId

Backend Payload Expected
{
  "questions": [
    {
      "question": "Accutane or Tetracycline",
      "customQuestion": false,
      "options": [
        { "label": "Yes", "type": "block" },
        { "label": "No", "type": "proceed" }
      ]
    },
    {
      "question": "Do you have your own equipment?",
      "customQuestion": true,
      "options": [
        { "label": "Yes", "type": "proceed" },
        { "label": "No", "type": "warn" }
      ]
    }
  ]
}


*/

import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Sidebar from "../../components/Sidebar";
import { useBusinessAuthContext } from "../commoncomponents/useBusinessAuthContext";
import API_URL from "../../api/API";
import { Lock, Undo2 } from "lucide-react";
import ConfirmRuleChangeModal from "../commoncomponents/ConfirmRuleChangeModal";
import WaiverStepLayout from "./WaiverStepLayout";
import WAIVER_STEP_RULES from "./WaiverStepConfig";

const ACTION_TYPES = [
  { value: "block", label: "Block Service" },
  { value: "warn", label: "Show Warning" },
  { value: "proceed", label: "Continue" }
];

const ACTION_STYLES = {
  block: "bg-red-100 text-red-700 border-red-200",
  warn: "bg-yellow-100 text-yellow-700 border-yellow-200",
  proceed: "bg-green-100 text-green-700 border-green-200"
};

const BusinessSmartTemplateStep5 = () => {

  const { business } = useBusinessAuthContext();
  const navigate = useNavigate();
  const { waiverId } = useParams();

  const [eligibility, setEligibility] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [customQuestions, setCustomQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modified, setModified] = useState(false);

  const [newQuestion, setNewQuestion] = useState("");
  const [originalQuestions, setOriginalQuestions] = useState([]);
  const [showConfirm, setShowConfirm] = useState(false);

  const [waiver, setWaiver] = useState(null); 
  const stepRules = WAIVER_STEP_RULES["step5"] || {};
  const statusRule = stepRules[waiver?.waiverStatus] || {};
  const isReadOnly = statusRule.readOnly ?? false;
  const readOnlyReason = statusRule.reason ?? "";

  const [saving, setSaving] = useState(false);

   // Load existing waiver data
  useEffect(() => {
    if (!business) {
      navigate("/business/login");
      return;
    }

    const fetchWaiver = async () => {
      try {
        const res = await fetch(
          `${API_URL.BUSINESS_WAIVER_BY_ID}/${waiverId}`,
          {
            headers: {
                Authorization: `Bearer ${business.token}`,
                "x-session-id": business.sessionId,               
                "x-frontend-url": window.location.pathname  
            },
          }
        );

        const data = await res.json();

        if (data.success) {          
          setWaiver(data.waiver);
        }
      } catch (err) {
        console.error("Failed to load waiver", err);
      } finally {
        setLoading(false);
      }
    };

    fetchWaiver();
  }, [business, waiverId, navigate]);

  // Fetch eligibility
  useEffect(() => {

    if (!business) {
      navigate("/business/login");
      return;
    }

    const fetchEligibility = async () => {
      try {

        const res = await fetch(
          `${API_URL.BUSINESS_ELIGIBILITY_BY_WAIVER}/${waiverId}`,
          {
            headers: {
                Authorization: `Bearer ${business.token}`,
                "x-session-id": business.sessionId,                 
                "x-frontend-url": window.location.pathname  
            }
          }
        );

        const data = await res.json();

        if (data.success) {

          const q = (data.eligibility.questions || []).map(question => ({
            ...question,
            question: question.question_adult_full,
            options: question.options_adult_full || []
          }));

          const system = q.filter(x => !x.customQuestion);
          const custom = q.filter(x => x.customQuestion);

          setEligibility(data.eligibility);
          setQuestions(system);
          setCustomQuestions(custom);
          setOriginalQuestions(
            q.filter(x => !x.customQuestion).map(q => ({
              _id: q._id,
              options: q.options_org_adult_full
            }))
          ); /* Must be set from Admin model? But what if admin model is updated */


        }

      } catch (err) {
        console.error("Failed loading eligibility", err);
      } finally {
        setLoading(false);
      }
    };

    fetchEligibility();

  }, [business, waiverId, navigate]);



  // Change rule type
  const handleOptionChange = (qIndex, oIndex, newType) => {

    const updated = questions.map((q, qi) => {

      if (qi !== qIndex) return q;

      return {
        ...q,
        options: (q.options || []).map((opt, oi) =>
          oi === oIndex ? { ...opt, type: newType } : opt
        )
      };

    });

    const rulesModified = detectRuleModification(updated);

    setQuestions(updated);
    setModified(rulesModified);

  };



  // Add custom question
  const addCustomQuestion = () => {

    if (!newQuestion.trim()) return;

    const newQ = {
      question: newQuestion,
      fieldType: "radio",
      customQuestion: true,
      options: [
        { label: "Yes", type: "proceed" },
        { label: "No", type: "proceed" }
      ]
    };

    setCustomQuestions([...customQuestions, newQ]);

    setNewQuestion("");

  };



  const removeCustom = (index) => {

    const updated = [...customQuestions];
    updated.splice(index, 1);
    setCustomQuestions(updated);

  };


  const handleContinue = () => {

    if (saving) return;

    if (modified) {
      setShowConfirm(true);
      return;
    }

    setSaving(true);
    saveStep5();
  };

  const saveStep5 = async () => {

    try {

      const res = await fetch(
        `${API_URL.BUSINESS_WAIVER_SMART_STEP_WAIVER_QUESTIONS}/${waiverId}`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${business.token}`,
            "x-session-id": business.sessionId,
            "x-frontend-url": window.location.pathname
          },
          body: JSON.stringify({
            questions: [...questions, ...customQuestions]
          })
        }
      );

      const data = await res.json();

      if (data.success) {
        navigate(`/business/waiverbuilder/smart/${waiverId}/step-6`);
      } else {
        alert(data.message);
      }

    } catch (err) {
      console.error("Failed saving step 5", err);
    } finally {
      setSaving(false);
    }

  };
  
 //compare with org questions options for  options_org_adult_full 
const detectRuleModification = (incomingQuestions) => {

  for (const incoming of incomingQuestions) {

    if (incoming.customQuestion) continue;

    const original = originalQuestions.find(
      q => q._id?.toString() === incoming._id?.toString()
    );

    if (!original) continue;

    const currentOptions = incoming.options || [];
    const originalOptions = original.options || [];

    for (let i = 0; i < currentOptions.length; i++) {

      if (currentOptions[i]?.type !== originalOptions[i]?.type) {
        return true;
      }

    }

  }

  return false;

};

 //Not comparing below with custom questions, but only with any changes on page load
/*  const detectRuleChanges = (updated) => {

    if (!originalQuestions.length) return false;

    const originalMap = new Map(
      originalQuestions.map(q => [q._id, q])
    );

    for (const q of updated) {

      const original = originalMap.get(q._id);
      if (!original) continue;

      for (let i = 0; i < q.options.length; i++) {

        if (q.options[i]?.type !== original.options?.[i]?.type) {
          return true;
        }

      }

    }

    return false;

  }; */

  if (loading) return <div>Loading...</div>;



  return (
    <>
    {/* 
    <div className="h-screen flex bg-background">

      <Sidebar />

      <div className="flex-1 overflow-auto py-10">*/ }
      <WaiverStepLayout  waiver={waiver} currentStep="step-5"  isReadOnly={isReadOnly} readOnlyReason={readOnlyReason} >
               

        <div className="max-w-3xl mx-auto px-6">

          <div className="flex items-center justify-between mb-6">
            {/* Back */}
            <button
              onClick={() =>
                navigate(`/business/waiverbuilder/smart/${waiverId}/step-4`)
              }
              className="text-sm text-muted-foreground"
            >
            ← Previous Step
            </button>

            {/* Next (only when readOnly) */}
            {isReadOnly && (
              <button
                onClick={() =>
                  navigate(`/business/waiverbuilder/smart/${waiverId}/step-6`)
                }
                className="text-sm text-muted-foreground"
              >
                Next Step →
              </button>
            )}
          </div>
          
          

          <h1 className="text-2xl font-semibold mb-1">
            Eligibility Builder
          </h1>

          <p className="text-sm text-muted-foreground mb-8">
            Step 5 of 7 · Configure screening requirements
          </p>


          {/* REQUIRED QUESTIONS */}

          <div className="bg-white rounded-xl shadow-sm p-6 mb-6">

            <div className="font-semibold mb-4">
              Required Rules
            </div>

            {questions.map((q, qi) => (

              <div key={q._id || qi} className="mb-6">

                <div className="text-sm font-medium mb-2">
                  {q.question}
                </div>

                {(q.options || []).map((opt, oi) => {

                  const originalQuestion = originalQuestions.find(
                    original =>
                      original._id?.toString() === q._id?.toString()
                  );

                  const originalOption = originalQuestion?.options?.find(
                    original => original._id?.toString() === opt._id?.toString()
                  );

                  const isChanged =
                    originalOption &&
                    opt.type !== originalOption.type;

                  return (
                    <div key={opt._id || oi} className="mb-2">

                      <div
                          className={`flex items-center justify-between border rounded-lg px-4 py-3 ${
                              isChanged ? "border-red-200" : ""
                          }`}
                      >

                      <span
                          className={`text-sm ${
                              isChanged ? "text-red-600 font-medium" : ""
                          }`}
                      >
                          {opt.label}
                      </span>

                      <div className="flex items-center gap-2">

                        {!isReadOnly && isChanged && (
                          <button
                            type="button"
                            onClick={() =>
                              handleOptionChange(
                                qi,
                                oi,
                                originalOption.type
                              )
                            }
                            title="Reset to default"
                            className="text-gray-400 hover:text-gray-700 p-1 rounded transition-colors"
                          >
                            <Undo2 size={15} />
                          </button>
                        )}

                        <select
                          value={opt.type}
                          
                          disabled={isReadOnly}
                          onChange={(e) =>
                            handleOptionChange(qi, oi, e.target.value)
                          }
                          className={`border rounded-full px-3 py-1 text-sm font-medium ${ACTION_STYLES[opt.type]}`}
                        >
                          {ACTION_TYPES.map(a => (
                            <option key={a.value} value={a.value}>
                              {a.label}
                            </option>
                          ))}
                        </select>

                        {!q.customQuestion && (
                          <Lock size={14} className="text-gray-400" />
                        )}
                                            
                      </div>

                    </div>

                    {isChanged && !isReadOnly && (
                        <div className="text-xs text-red-600 mt-1 px-4">
                            You’re changing the default screening outcome. This may
                            allow a client to proceed under different conditions than
                            originally configured.
                        </div>
                    )}

                  </div>
                  );

                })}

            </div>

            ))}

          </div>



          {/* CUSTOM QUESTIONS */}

          <div className="bg-white rounded-xl shadow-sm p-6">

            <div className="font-semibold mb-4">
              Custom Questions (Optional)
            </div>

            {customQuestions.map((q, qi) => (

              <div
                key={qi}
                className="border rounded-lg px-4 py-3 mb-3"
              >

                <div className="flex justify-between mb-2">

                  <span>{q.question}</span>

                  {!isReadOnly && (
                  <button
                    onClick={() => removeCustom(qi)}
                    className="text-red-500 text-sm"
                  >
                    Delete
                  </button>
                   )}

                </div>

                {(q.options || []).map((opt, oi) => (

                  <div
                    key={oi}
                    className="flex justify-between items-center border rounded-md px-3 py-2 mb-2"
                  >

                    <span>{opt.label}</span>

                    <select
                      value={opt.type}
                      disabled={isReadOnly}
                      onChange={(e) => {

                        const updated = [...customQuestions];
                        updated[qi].options[oi].type = e.target.value;

                        setCustomQuestions(updated);

                      }}
                      className={`border rounded-full px-3 py-1 text-sm ${ACTION_STYLES[opt.type]}`}
                    >
                      {ACTION_TYPES.map(a => (
                        <option key={a.value} value={a.value}>
                          {a.label}
                        </option>
                      ))}
                    </select>

                  </div>

                ))}

              </div>

            ))}

            {!isReadOnly && ( 
            <div className="flex gap-3 mt-4">

              <input
                value={newQuestion}
                onChange={(e) => setNewQuestion(e.target.value)}
                placeholder="Add a question"
                className="flex-1 border rounded-lg px-4 py-2"
              />

              <button
                onClick={addCustomQuestion}
                className="bg-gray-100 px-5 rounded-lg"
              >
                Add
              </button>

            </div>
             )}
          </div>



          {/* WARNING */}

          {modified && !isReadOnly && (

            <div className="text-yellow-700 text-sm mt-4">
              You have modified default screening rules.
              This may affect liability coverage.
            </div>

          )}



          {/* CONTINUE */}
          {!isReadOnly && ( 
          <div className="flex justify-end mt-8">

            <button
              onClick={handleContinue}
              disabled={saving}
              className={`px-6 py-3 rounded-full font-medium ${
                saving
                  ? "bg-yellow-100 text-yellow-400 cursor-not-allowed"
                  : "bg-yellow-500 hover:bg-yellow-600 text-black"
              }`}
            >
              {saving ? "Saving..." : "Continue to Waiver Builder →"}
            </button>

          </div>
          )}
        </div>

      </WaiverStepLayout>  
          

      <ConfirmRuleChangeModal
        open={showConfirm}
        onConfirm={async () => {
            setShowConfirm(false);
            setSaving(true);
            try {
                // Mark eligibility changes as accepted
                const res = await fetch(
                    `${API_URL.BUSINESS_WAIVER_ACCEPT_ELIGIBILITY_CHANGES}/${waiverId}`,
                    {
                        method: "PATCH",
                        headers: {
                            Authorization: `Bearer ${business.token}`,
                            "x-session-id": business.sessionId,
                            "x-frontend-url": window.location.pathname,
                        }
                    }
                );
                const data = await res.json();
                if (!data.success) {
                    alert(
                        data.message ||
                        "Failed to confirm eligibility changes."
                    );
                    return;
                }
                // Now save Step 5 and continue
                saveStep5();
            } catch (err) {
                console.error(
                    "Failed to confirm eligibility changes:",
                    err
                );
                alert("Failed to confirm eligibility changes.");
            } finally {
                setSaving(false);
            }
        }}
        onCancel={() => {
            setShowConfirm(false);
            setSaving(false);
        }}
    />
    </>

  );

};

export default BusinessSmartTemplateStep5;